import { useEffect, useState } from "react";
import { ArrowUpRight, BookOpen, Droplets, Dumbbell, GraduationCap, HeartPulse, LockKeyhole, Globe2, Target, Utensils } from "lucide-react";
import type { RecordItem } from "./useRecordHistory";
import { recordDimensions, recordImages, recordText, recordTimestamp } from "./recordPresentation";

interface Props {
  record: RecordItem;
  onOpen: () => void;
  onPreview: (url: string) => void;
}

export default function RecordPost({ record, onOpen, onPreview }: Props) {
  const [expanded, setExpanded] = useState(false);
  const { title, content } = recordText(record);
  useEffect(() => setExpanded(false), [record.key, content]);
  const characters = Array.from(content);
  const long = characters.length > 280;
  const text = long && !expanded ? `${characters.slice(0, 280).join("").trimEnd()}…` : content;
  const images = recordImages(record);
  const dimensions = recordDimensions(record);
  const category = record.category || record.kind;
  const Icon = category === "meal" ? Utensils : category === "exercise" ? Dumbbell :
    category === "water" ? Droplets : category === "review" ? Target :
    category === "health" || record.kind.startsWith("health_") ? HeartPulse :
    category === "study" || record.kind.startsWith("study_") ? GraduationCap : BookOpen;
  const privacy = record.kind === "note" && typeof record.data?.is_public === "boolean";
  return (
    <article className="rc-post" aria-label={`${record.label || "记录"} · ${record.day}`}>
      <header className="rc-post-header">
        <span className="rc-post-avatar" aria-hidden="true"><Icon size={21} /></span>
        <div className="rc-post-identity">
          <strong>{record.label || "记录"}</strong>
          <div className="rc-post-meta">
            <time dateTime={record.time || record.day}>{recordTimestamp(record)}</time>
            {privacy && <span className="rc-post-privacy">{record.data.is_public ? <Globe2 size={12} aria-hidden="true" /> : <LockKeyhole size={12} aria-hidden="true" />}{record.data.is_public ? "公开" : "仅自己"}</span>}
          </div>
        </div>
      </header>
      <div className="rc-post-body">
        {title && <h3>{title}</h3>}
        {text && <p className="rc-post-text">{text}</p>}
        {!title && !text.trim() && images.length === 0 && <p className="rc-post-empty">暂未填写内容</p>}
        {long && <button type="button" className="rc-post-expand" aria-expanded={expanded} onClick={() => setExpanded(value => !value)}>{expanded ? "收起正文" : "展开全文"}</button>}
        {images.length > 0 && <div data-count={images.length} className={`rc-post-images ${images.length === 1 ? "rc-post-single-image" : ""}`}>
          {images.slice(0, 9).map((url, index) => {
            const remaining = index === 8 ? images.length - 9 : 0;
            return <button type="button" className="rc-post-image" key={`${index}:${url}`} aria-label={remaining > 0 ? `查看全部 ${images.length} 张图片` : `查看第 ${index + 1} 张图片`} onClick={() => remaining > 0 ? onOpen() : onPreview(url)}>
              <img src={url} alt={`记录图片 ${index + 1}`} loading="lazy" />
              {remaining > 0 && <span className="rc-post-image-more">+{remaining}</span>}
            </button>;
          })}
        </div>}
      </div>
      <footer className="rc-post-footer">
        <div className="rc-post-dimensions" aria-label="相关维度">{dimensions.map(dimension => <span key={dimension.key}>{dimension.label}</span>)}</div>
        <button type="button" className="rc-row rc-post-open" onClick={onOpen}>查看详情 <ArrowUpRight size={15} aria-hidden="true" /></button>
      </footer>
    </article>
  );
}
